import { DateTime as DT } from 'luxon';
import type { EventInput } from '@fullcalendar/core';
import type { TaskType } from '@econtractor/types';
import { TaskTypeStatus } from '@econtractor/types';
import { isOverdueTask } from '../../Board/Kanban/KanbanCard/helpers';
import { canEditTaskTerm } from '../../shared/helpers/taskDueDateHelpers';

const STATUS_TONE: Record<number, string> = {
    [TaskTypeStatus.NEW]: 'new',
    [TaskTypeStatus.INWORK]: 'inwork',
    [TaskTypeStatus.COMPLETED]: 'completed',
    [TaskTypeStatus.ACCEPTED]: 'accepted',
    [TaskTypeStatus.CANCELLED]: 'cancelled',
    [TaskTypeStatus.REJECT]: 'cancelled',
};

const isoDay = (dt: DT): string | undefined => dt.toISODate() ?? undefined;

const isUnrelatedTask = (task: TaskType, userId: number): boolean => {
    if (!(userId > 0)) return false;
    return (
        userId !== Number(task.userId) &&
        userId !== Number(task.taskExecutor) &&
        userId !== Number(task.taskController)
    );
};

export const toFullCalendarEvents = (
    tasks: TaskType[],
    userId: number,
    monthView = false,
): EventInput[] => {
    const events: EventInput[] = [];

    for (const task of tasks) {
        if (task.taskKanbanHide) continue;

        const dateEnd = Number(task.dateEnd || 0);
        if (!dateEnd) continue;

        const endDt = DT.fromMillis(dateEnd, { zone: 'local' });
        if (!endDt.isValid) continue;

        const tone = STATUS_TONE[Number(task.taskStatus)] || 'new';
        const isExecutor = userId > 0 && userId === Number(task.taskExecutor);
        const isController = userId > 0 && userId === Number(task.taskController);
        const canDrag = canEditTaskTerm(task, userId);
        const classNames = [`fc-event-task--${tone}`];
        if (isUnrelatedTask(task, userId)) classNames.push('fc-event-task--outline');
        if (isOverdueTask(task)) classNames.push('fc-event-task--overdue');

        const base: EventInput = {
            id: String(task.ID),
            title: String(task.taskName || ''),
            editable: canDrag,
            startEditable: canDrag,
            durationEditable: false,
            classNames,
            extendedProps: {
                isExecutor,
                isController,
                categoryName: task.categoryName || '',
            },
        };

        if (task.multiDay) {
            const startMs = Number(task.dateStart) || dateEnd;
            const startDt = DT.fromMillis(startMs, { zone: 'local' }).startOf('day');
            const exclusiveEnd = endDt.startOf('day').plus({ days: 1 });
            events.push({
                ...base,
                allDay: true,
                durationEditable: canDrag,
                start: isoDay(startDt),
                end: isoDay(exclusiveEnd),
            });
            continue;
        }

        if (task.withTime) {
            const startMs = Number(task.dateStart) || endDt.startOf('day').toMillis();
            let startDt = DT.fromMillis(startMs, { zone: 'local' });
            if (!startDt.isValid || startDt >= endDt) {
                startDt = endDt.minus({ minutes: 30 });
            }
            const timeLabel = `${startDt.toFormat('HH:mm')}–${endDt.toFormat('HH:mm')}`;
            if (monthView) {
                const dayStart = endDt.startOf('day');
                events.push({
                    ...base,
                    allDay: true,
                    durationEditable: canDrag,
                    start: isoDay(dayStart),
                    end: isoDay(dayStart.plus({ days: 1 })),
                    extendedProps: { ...base.extendedProps, timeLabel },
                });
                continue;
            }
            events.push({
                ...base,
                allDay: false,
                durationEditable: canDrag,
                start: startDt.toJSDate(),
                end: endDt.toJSDate(),
            });
            continue;
        }

        const dayStart = endDt.startOf('day');
        events.push({
            ...base,
            allDay: true,
            durationEditable: canDrag,
            start: isoDay(dayStart),
            end: isoDay(dayStart.plus({ days: 1 })),
        });
    }

    return events;
};
